import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  Checkbox,
  Collapse,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Spin,
  Switch,
  Table,
  Tag,
  Upload,
  message,
} from 'antd';
import {
  adminFetch,
  adminUpload,
  AdminUnauthorized,
  getAdminToken,
} from './admin-api';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Any = any;

const fmt = (v: Any) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

const BUNKER_DEFAULTS = {
  bunker_model: '',
  modification: '',
  number_of_wheels: 4,
  speed: 25,
  wheel_type: '500/60-22,5',
  width: 2.5,
  carrying_capacity: 16,
  volume: 20,
  guarantee: 24,
  current_price: 3000000,
  dealer_price: 3000000,
  previous_price: 3000000,
  can_configured: true,
  active: true,
  height: 420,
  power: 120,
  performance: '450/600',
  description: '',
};

const MOD_DEFAULTS = {
  modification: '',
  number_of_wheels: 4,
  speed: 25,
  wheel_type: '500/60-22,5',
  width: 2.5,
  carrying_capacity: 16,
  volume: 20,
  guarantee: 24,
  current_price: 3000000,
  dealer_price: 3000000,
  previous_price: 3000000,
  single_circuit_braking: true,
  mechanical_slide_gates: true,
  awning_system: true,
  seeder_loading_kit: false,
  weighing_with_thermal_print: false,
  two_sections: false,
  hopper: false,
};

const MOD_FLAGS: Array<[string, string]> = [
  ['single_circuit_braking', 'Одноконтурный тормоз'],
  ['mechanical_slide_gates', 'Механические заслонки'],
  ['awning_system', 'Тентовая система'],
  ['seeder_loading_kit', 'Комплект загрузки сеялок'],
  ['weighing_with_thermal_print', 'Весы с термопечатью'],
  ['two_sections', 'Двухсекционный'],
  ['hopper', 'Бункер-накопитель'],
];

const Num = ({
  name,
  label,
  step = 1,
  money = false,
}: {
  name: string;
  label: string;
  step?: number;
  money?: boolean;
}) => (
  <Form.Item
    name={name}
    label={label}
    rules={[{ required: true, message: 'Обязательно' }]}>
    <InputNumber
      min={step < 1 ? 0.1 : 1}
      step={step}
      className="w-full"
      formatter={money ? fmt : undefined}
    />
  </Form.Item>
);

export const AdminCatalogView = () => {
  const [authed, setAuthed] = useState(!!getAdminToken());
  const [pwd, setPwd] = useState('');
  const [loading, setLoading] = useState(false);
  const [bunkers, setBunkers] = useState<Any[]>([]);
  const [mods, setMods] = useState<Any[]>([]);
  const [options, setOptions] = useState<Any[]>([]);
  const [showPrices, setShowPrices] = useState(false);

  const [bunkerForm] = Form.useForm();
  const [modForm] = Form.useForm();
  const [editingBunker, setEditingBunker] = useState<Any | null>(null);
  const [editingMod, setEditingMod] = useState<Any | null>(null);
  const [saving, setSaving] = useState(false);
  const [imgBusy, setImgBusy] = useState(false);

  const load = async (token?: string) => {
    setLoading(true);
    try {
      const [b, m, o, p] = await Promise.all([
        adminFetch('/admin/bunkers', { token }),
        adminFetch('/admin/bunker_modifications', { token }),
        adminFetch('/admin/configuration_items', { token }),
        adminFetch('/admin/prices', { token }),
      ]);
      setBunkers(b || []);
      setMods(m || []);
      setOptions(o || []);
      setShowPrices(!!p?.show_prices);
      setAuthed(true);
      if (token) localStorage.setItem('admin_token', token);
    } catch (e) {
      if (e instanceof AdminUnauthorized) {
        message.error('Неверный пароль администратора');
        localStorage.removeItem('admin_token');
        setAuthed(false);
      } else {
        message.error('Не удалось загрузить каталог');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (getAdminToken()) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleShowPrices = async (checked: boolean) => {
    setShowPrices(checked);
    try {
      await adminFetch('/admin/prices', {
        method: 'PATCH',
        body: { show_prices: checked },
      });
    } catch (e) {
      message.error((e as Error).message);
      setShowPrices(!checked);
    }
  };

  // --- Модель (Bunker) ---
  const openBunker = (record?: Any) => {
    setEditingBunker(record || {});
    bunkerForm.setFieldsValue(record ? record : BUNKER_DEFAULTS);
  };

  const saveBunker = async () => {
    const values = await bunkerForm.validateFields();
    setSaving(true);
    try {
      const path = editingBunker?.id
        ? `/admin/bunkers/${editingBunker.id}`
        : '/admin/bunkers';
      await adminFetch(path, {
        method: editingBunker?.id ? 'PATCH' : 'POST',
        body: { bunker: values },
      });
      message.success('Модель сохранена');
      setEditingBunker(null);
      await load();
    } catch (e) {
      message.error((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const deleteBunker = async (id: number) => {
    try {
      await adminFetch(`/admin/bunkers/${id}`, { method: 'DELETE' });
      message.success('Модель удалена');
      await load();
    } catch (e) {
      message.error((e as Error).message);
    }
  };

  // Картинка модели (ActiveStorage → Cloudinary). Доступно только у сохранённой модели.
  const syncBunker = (updated: Any) => {
    setEditingBunker(updated);
    setBunkers((prev) => prev.map((b) => (b.id === updated.id ? updated : b)));
  };

  const uploadBunkerImage = async (file: File) => {
    if (!editingBunker?.id) return;
    setImgBusy(true);
    try {
      syncBunker(
        await adminUpload(`/admin/bunkers/${editingBunker.id}/image`, file)
      );
      message.success('Изображение обновлено');
    } catch (e) {
      message.error((e as Error).message);
    } finally {
      setImgBusy(false);
    }
  };

  const removeBunkerImage = async () => {
    if (!editingBunker?.id) return;
    setImgBusy(true);
    try {
      syncBunker(
        await adminFetch(`/admin/bunkers/${editingBunker.id}/image`, {
          method: 'DELETE',
        })
      );
      message.success('Стандартное изображение возвращено');
    } catch (e) {
      message.error((e as Error).message);
    } finally {
      setImgBusy(false);
    }
  };

  // --- Модификация ---
  const openMod = (bunkerId: number, record?: Any) => {
    setEditingMod(record || { bunker_id: bunkerId });
    modForm.setFieldsValue(
      record ? record : { ...MOD_DEFAULTS, bunker_id: bunkerId }
    );
  };

  const saveMod = async () => {
    const values = await modForm.validateFields();
    const parent = bunkers.find((b) => b.id === values.bunker_id);
    const body = { ...values, bunker_model: parent?.bunker_model || '' };
    setSaving(true);
    try {
      const path = editingMod?.id
        ? `/admin/bunker_modifications/${editingMod.id}`
        : '/admin/bunker_modifications';
      await adminFetch(path, {
        method: editingMod?.id ? 'PATCH' : 'POST',
        body: { bunker_modification: body },
      });
      message.success('Модификация сохранена');
      setEditingMod(null);
      await load();
    } catch (e) {
      message.error((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const deleteMod = async (id: number) => {
    try {
      await adminFetch(`/admin/bunker_modifications/${id}`, {
        method: 'DELETE',
      });
      message.success('Модификация удалена');
      await load();
    } catch (e) {
      message.error((e as Error).message);
    }
  };

  // Инлайн-редактирование цены модификации
  const setModPriceLocal = (id: number, price: number) =>
    setMods((prev) =>
      prev.map((m) => (m.id === id ? { ...m, current_price: price } : m))
    );

  const saveModPrice = async (m: Any) => {
    try {
      await adminFetch(`/admin/bunker_modifications/${m.id}`, {
        method: 'PATCH',
        body: { bunker_modification: { current_price: m.current_price } },
      });
      message.success('Цена сохранена', 0.8);
    } catch (e) {
      message.error((e as Error).message);
      await load();
    }
  };

  // Матрица совместимости: статус опции у модификации
  const setRule = async (modId: number, ciId: number, status: string) => {
    setMods((prev) =>
      prev.map((m) =>
        m.id === modId
          ? { ...m, rules: { ...(m.rules || {}), [ciId]: status } }
          : m
      )
    );
    try {
      await adminFetch(`/admin/bunker_modifications/${modId}/rule`, {
        method: 'PATCH',
        body: { configuration_item_id: ciId, status },
      });
    } catch (e) {
      message.error((e as Error).message);
      await load();
    }
  };

  if (!authed) {
    return (
      <div className="container max-w-[420px] py-16">
        <h2 className="text-[20px] font-semibold mb-4">Админка каталога</h2>
        <Input.Password
          placeholder="Пароль администратора"
          value={pwd}
          onChange={(e) => setPwd(e.target.value)}
          onPressEnter={() => load(pwd)}
          className="mb-3"
        />
        <Button type="primary" block loading={loading} onClick={() => load(pwd)}>
          Войти
        </Button>
      </div>
    );
  }

  // Матрица опций для конкретной модификации
  const matrix = (m: Any) => (
    <Table
      size="small"
      rowKey="id"
      pagination={false}
      dataSource={options}
      columns={[
        {
          title: 'Опция',
          dataIndex: 'display_name',
          render: (v: string, o: Any) => (
            <span>
              {v}
              {!o.visible && (
                <Tag className="ml-2" color="default">
                  скрыта
                </Tag>
              )}
            </span>
          ),
        },
        {
          title: 'Есть',
          width: 90,
          align: 'center' as const,
          render: (_: Any, o: Any) => (
            <Checkbox
              checked={(m.rules?.[o.id] || 'disabled') === 'enabled'}
              onChange={(e) =>
                setRule(m.id, o.id, e.target.checked ? 'enabled' : 'disabled')
              }
            />
          ),
        },
        {
          title: 'За доплату',
          width: 110,
          align: 'center' as const,
          render: (_: Any, o: Any) => (
            <Checkbox
              checked={(m.rules?.[o.id] || 'disabled') === 'charged'}
              onChange={(e) =>
                setRule(m.id, o.id, e.target.checked ? 'charged' : 'disabled')
              }
            />
          ),
        },
      ]}
    />
  );

  const modColumns = [
    { title: 'Модификация', dataIndex: 'modification' },
    {
      title: 'Цена, ₽',
      width: 200,
      render: (_: Any, m: Any) => (
        <InputNumber
          min={1}
          step={1000}
          value={m.current_price}
          formatter={fmt}
          className="w-[160px]"
          onChange={(v) => setModPriceLocal(m.id, Number(v))}
          onBlur={() => saveModPrice(m)}
          onPressEnter={() => saveModPrice(m)}
        />
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 170,
      render: (_: Any, m: Any) => (
        <div className="flex gap-2">
          <Button size="small" onClick={() => openMod(m.bunker_id, m)}>
            Изменить
          </Button>
          <Popconfirm
            title="Удалить модификацию?"
            okText="Удалить"
            cancelText="Отмена"
            onConfirm={() => deleteMod(m.id)}>
            <Button size="small" danger>
              Удалить
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div className="container pb-[120px]">
      <div className="flex items-center justify-between my-6">
        <h2 className="text-[22px] font-semibold">Модели и модификации</h2>
        <div className="flex items-center gap-5">
          <span className="flex items-center gap-2">
            <Switch checked={showPrices} onChange={toggleShowPrices} />
            Показывать цены на сайте
          </span>
          <Link to="/admin/options" className="text-[#007E3C] underline">
            → Справочник опций
          </Link>
        </div>
      </div>

      <Button
        type="primary"
        className="mb-4"
        style={{ background: '#66B28A' }}
        onClick={() => openBunker()}>
        + Добавить модель
      </Button>

      <Spin spinning={loading}>
        <Collapse
          items={bunkers.map((b) => ({
            key: String(b.id),
            label: (
              <div className="flex items-center gap-3">
                <span className="font-semibold">{b.bunker_model}</span>
                <span className="text-[#888]">{b.modification}</span>
                {!b.active && <Tag color="default">скрыта</Tag>}
                {!b.can_configured && <Tag color="orange">без опций</Tag>}
                <Tag>{fmt(b.current_price)} ₽</Tag>
                <span className="text-[#999] text-[12px]">
                  модификаций: {b.modifications_count}
                </span>
              </div>
            ),
            extra: (
              <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                <Button size="small" onClick={() => openBunker(b)}>
                  Изменить
                </Button>
                <Popconfirm
                  title="Удалить модель?"
                  description="Вместе с её модификациями и правилами."
                  okText="Удалить"
                  cancelText="Отмена"
                  onConfirm={() => deleteBunker(b.id)}>
                  <Button size="small" danger>
                    Удалить
                  </Button>
                </Popconfirm>
              </div>
            ),
            children: (
              <>
                <Table
                  size="small"
                  rowKey="id"
                  pagination={false}
                  columns={modColumns}
                  dataSource={mods.filter((m) => m.bunker_id === b.id)}
                  expandable={{
                    expandedRowRender: (m: Any) => matrix(m),
                    rowExpandable: () => true,
                  }}
                />
                <Button className="mt-3" onClick={() => openMod(b.id)}>
                  + Добавить модификацию
                </Button>
              </>
            ),
          }))}
        />
      </Spin>

      {/* Форма модели */}
      <Modal
        title={editingBunker?.id ? 'Изменить модель' : 'Новая модель'}
        open={!!editingBunker}
        onCancel={() => setEditingBunker(null)}
        onOk={saveBunker}
        confirmLoading={saving}
        okText="Сохранить"
        cancelText="Отмена"
        width={720}>
        <Form
          form={bunkerForm}
          layout="vertical"
          className="grid grid-cols-2 gap-x-4">
          {editingBunker?.id && (
            <div className="col-span-2 mb-3">
              <div className="mb-1 font-medium">Изображение модели</div>
              <div className="flex items-center gap-4">
                <img
                  src={editingBunker.bunker_image_url}
                  alt=""
                  className="w-[160px] h-[110px] object-contain border rounded bg-[#fafafa]"
                />
                <div className="flex flex-col items-start gap-2">
                  <Upload
                    accept="image/png,image/jpeg"
                    showUploadList={false}
                    beforeUpload={(file) => {
                      uploadBunkerImage(file as File);
                      return false;
                    }}>
                    <Button loading={imgBusy}>Загрузить / заменить</Button>
                  </Upload>
                  {editingBunker.bunker_image_custom && (
                    <Button
                      size="small"
                      danger
                      loading={imgBusy}
                      onClick={removeBunkerImage}>
                      Вернуть стандартную
                    </Button>
                  )}
                  <span className="text-[12px] text-[#999]">
                    PNG/JPEG, до 2 МБ
                  </span>
                </div>
              </div>
            </div>
          )}
          <Form.Item name="bunker_model" label="Модель" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="modification" label="Название" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Num name="number_of_wheels" label="Кол-во колёс" />
          <Num name="speed" label="Скорость, км/ч" />
          <Form.Item name="wheel_type" label="Тип шин" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Num name="guarantee" label="Гарантия, мес." />
          <Num name="width" label="Ширина, м" step={0.1} />
          <Num name="carrying_capacity" label="Грузоподъёмность, т" step={0.1} />
          <Num name="volume" label="Объём, м³" step={0.1} />
          <Num name="height" label="Высота, см" />
          <Num name="power" label="Мощность, л.с." />
          <Form.Item name="performance" label="Производительность" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Num name="current_price" label="Цена, ₽" step={1000} money />
          <Num name="dealer_price" label="Дилерская, ₽" step={1000} money />
          <Num name="previous_price" label="Прежняя, ₽" step={1000} money />
          <Form.Item name="can_configured" label="Конфигурируемая" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="active" label="В каталоге" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="description" label="Описание" className="col-span-2">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Форма модификации */}
      <Modal
        title={editingMod?.id ? 'Изменить модификацию' : 'Новая модификация'}
        open={!!editingMod}
        onCancel={() => setEditingMod(null)}
        onOk={saveMod}
        confirmLoading={saving}
        okText="Сохранить"
        cancelText="Отмена"
        width={720}>
        <Form form={modForm} layout="vertical" className="grid grid-cols-2 gap-x-4">
          <Form.Item name="bunker_id" label="Модель" rules={[{ required: true }]}>
            <Select
              options={bunkers.map((b) => ({
                value: b.id,
                label: `${b.bunker_model} — ${b.modification}`,
              }))}
            />
          </Form.Item>
          <Form.Item name="modification" label="Название модификации" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Num name="number_of_wheels" label="Кол-во колёс" />
          <Num name="speed" label="Скорость, км/ч" />
          <Form.Item name="wheel_type" label="Тип шин" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Num name="guarantee" label="Гарантия, мес." />
          <Num name="width" label="Ширина, м" step={0.1} />
          <Num name="carrying_capacity" label="Грузоподъёмность, т" step={0.1} />
          <Num name="volume" label="Объём, м³" step={0.1} />
          <Num name="current_price" label="Цена, ₽" step={1000} money />
          <Num name="dealer_price" label="Дилерская, ₽" step={1000} money />
          <Num name="previous_price" label="Прежняя, ₽" step={1000} money />
          <div className="col-span-2 mt-2 mb-1 font-semibold">
            Базовая комплектация
          </div>
          {MOD_FLAGS.map(([key, label]) => (
            <Form.Item key={key} name={key} label={label} valuePropName="checked">
              <Switch />
            </Form.Item>
          ))}
        </Form>
      </Modal>
    </div>
  );
};
